import React from "react";

import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { LEVEL_BAND_HEX } from "../../Utils/Helper/levelBandColors";

type LevelTrendPoint = {
  name: string;
  value: number | null;
  /** Real-data-only detail (assessment name/count + completed date) shown on node hover — never set for sample/preview series. */
  tooltip?: string;
};

type EndLabel = {
  /** e.g. "AL 3" */
  badgeText: string;
  badgeBg: string;
  badgeTextColor: string;
  trend: "up" | "down" | "flat";
  trendColor: string;
};

type LevelTrendChartProps = {
  data: LevelTrendPoint[];
  color: string;
  height?: number;
  /** Fixed 0-5 scale by default — matches AL/RL levels and 5-point poll scores. */
  domain?: [number, number];
  ticks?: number[];
  /** When set, renders colored "AL ?/AL 0../RL 0.." badge ticks instead of the plain dot scale, and defaults the domain to include the Unknown (-1) band. */
  levelPrefix?: "AL" | "RL";
  /** Plain "0".."5" numeric Y-axis labels instead of the colored dot scale — for generic 0-5 point scores (e.g. sponsor engagement composite). */
  numericYAxis?: boolean;
  /** Small level badge + trend glyph rendered directly at the last plotted point, in place of a separate legend. */
  endLabel?: EndLabel;
  /** Override font size for X-axis and Y-axis tick labels (default 12 for X, 11 for numeric Y). */
  tickFontSize?: number;
  /** Override LineChart margin — use when aligning chart plot area with another chart. */
  chartMargin?: { top: number; right: number; bottom: number; left: number };
  /** Explicit XAxis height in px — use to pin the plot-area bottom to a known pixel position. */
  xAxisHeight?: number;
};

const ChartTooltip: React.FC<{
  active?: boolean;
  payload?: { payload: LevelTrendPoint }[];
}> = ({ active, payload }) => {
  const point = payload?.[0]?.payload;
  if (!active || !point?.tooltip) return null;
  return (
    <div className="rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs leading-snug text-gray-700 shadow-lg">
      <p className="m-0 font-semibold text-gray-900">{point.name}</p>
      <p className="m-0 mt-0.5 text-gray-600">{point.tooltip}</p>
    </div>
  );
};

/** Same recharts line-chart pattern as AccountWorkspace's AssessmentsTrends card — colored dot Y-axis ticks, halo'd data points. */
const LevelTrendChart: React.FC<LevelTrendChartProps> = ({
  data,
  color,
  height,
  domain,
  ticks,
  levelPrefix,
  numericYAxis,
  endLabel,
  tickFontSize,
  chartMargin,
  xAxisHeight,
}) => {
  const resolvedDomain: [number, number] =
    domain ?? (levelPrefix ? [-1, 5] : [0, 5]);
  const resolvedTicks =
    ticks ?? (levelPrefix ? [-1, 0, 1, 2, 3, 4, 5] : [0, 1, 2, 3, 4, 5]);
  // AL/RL and numeric 0-5 charts get extra vertical room so every band stays
  // legible and clearly separated from its neighbors.
  const resolvedHeight =
    height ?? (levelPrefix ? 220 : numericYAxis ? 200 : 140);

  let lastPlottedIndex = -1;
  for (let i = data.length - 1; i >= 0; i -= 1) {
    if (data[i]?.value != null) {
      lastPlottedIndex = i;
      break;
    }
  }

  return (
    <div className="w-full" style={{ height: resolvedHeight }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={
            chartMargin ??
            (levelPrefix
              ? { top: 12, right: 8, bottom: 4, left: 4 }
              : { top: 10, right: 8, bottom: 4, left: 4 })
          }
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e6ed" vertical />
          <XAxis
            dataKey="name"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: tickFontSize ?? 12, fill: "#4D4D4D" }}
            {...(xAxisHeight != null ? { height: xAxisHeight } : {})}
          />
          <YAxis
            domain={resolvedDomain}
            ticks={resolvedTicks}
            width={levelPrefix ? 46 : numericYAxis ? 26 : 28}
            axisLine={false}
            tickLine={false}
            tick={
              levelPrefix
                ? ({
                    x,
                    y,
                    payload,
                  }: {
                    x: number;
                    y: number;
                    payload: { value: number };
                  }) => {
                    const value = payload.value;
                    const isUnknown = value === -1;
                    const band =
                      LEVEL_BAND_HEX[isUnknown ? "unknown" : String(value)];
                    const label = isUnknown
                      ? `${levelPrefix} ?`
                      : `${levelPrefix} ${value}`;
                    return (
                      <g transform={`translate(${x},${y})`}>
                        <rect
                          x={-42}
                          y={-9}
                          width={38}
                          height={18}
                          rx={3}
                          fill={band.bg}
                        />
                        <text
                          x={-23}
                          y={4}
                          textAnchor="middle"
                          fontSize={10}
                          fontWeight={700}
                          fill={band.text}
                        >
                          {label}
                        </text>
                      </g>
                    );
                  }
                : numericYAxis
                  ? ({
                      x,
                      y,
                      payload,
                    }: {
                      x: number;
                      y: number;
                      payload: { value: number };
                    }) => (
                      <text
                        x={x - 8}
                        y={y}
                        dy={4}
                        textAnchor="middle"
                        fontSize={tickFontSize ?? 11}
                        fontWeight={600}
                        fill="#6B7280"
                      >
                        {payload.value}
                      </text>
                    )
                  : ({ x, y }: { x: number; y: number }) => (
                      <g transform={`translate(${x},${y})`}>
                        <circle cx={-10} cy={0} r={5} fill={color} />
                      </g>
                    )
            }
          />
          <Tooltip
            cursor={{ stroke: "#CBD5E1", strokeWidth: 1 }}
            content={<ChartTooltip />}
          />
          <Line
            type="linear"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            connectNulls={false}
            dot={({
              cx,
              cy,
              index,
            }: {
              cx?: number;
              cy?: number;
              index?: number;
            }) => {
              if (cx == null || cy == null) return <g />;
              const showEndLabel =
                endLabel != null && index === lastPlottedIndex;
              return (
                <g>
                  <circle cx={cx} cy={cy} r={8} fill={color} opacity={0.2} />
                  <circle
                    cx={cx}
                    cy={cy}
                    r={4}
                    fill="white"
                    stroke={color}
                    strokeWidth={2}
                  />
                  {showEndLabel ? (
                    <g>
                      <rect
                        x={cx - 46}
                        y={cy - 23}
                        width={36}
                        height={18}
                        rx={4}
                        fill={endLabel.badgeBg}
                        stroke={endLabel.badgeTextColor}
                        strokeWidth={1.25}
                      />
                      <text
                        x={cx - 28}
                        y={cy - 10.5}
                        textAnchor="middle"
                        fontSize={11}
                        fontWeight={800}
                        fill={endLabel.badgeTextColor}
                      >
                        {endLabel.badgeText}
                      </text>
                      {endLabel.trend === "up" ? (
                        <polygon
                          points={`${cx - 5},${cy - 20} ${cx - 9},${cy - 12} ${cx - 1},${cy - 12}`}
                          fill={endLabel.trendColor}
                        />
                      ) : endLabel.trend === "down" ? (
                        <polygon
                          points={`${cx - 5},${cy - 12} ${cx - 9},${cy - 20} ${cx - 1},${cy - 20}`}
                          fill={endLabel.trendColor}
                        />
                      ) : (
                        <rect
                          x={cx - 9}
                          y={cy - 17}
                          width={8}
                          height={2.5}
                          rx={1.25}
                          fill={endLabel.trendColor}
                        />
                      )}
                    </g>
                  ) : null}
                </g>
              );
            }}
            activeDot={{ r: 7, stroke: color, strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default LevelTrendChart;
